import { lazy, Suspense, useRef } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { Main, Wrapper } from './content.styles';

import PageWrapper from '../../page_wrapper.styles';

import Footer from '../../footer';
import LoadingText from '../../loading_text';
import PermissionGate from '../../permission_gate';
import ScrollToTop from '../../scroll_to_top';
import Title from '../../title';
import ConfigureBanner from './configure_banner';
import Header from './header';
import OpenWidgetHandler from './openwidget_handler';
import PlanSubscriptionIssuesBanner from './plan_subscription_issues_banner';
import SelectIntegration from './select_integration';

import { MODE } from '../../../env';

const shouldDisplayIngredients = MODE === 'saladstory-test' || MODE === 'saladstory-production';

const AccountRolesPage = lazy(() => import('./account_roles'));
const AccountsPage = lazy(() => import('./accounts'));
const BusinessesPage = lazy(() => import('./businesses'));
const CardsPage = lazy(() => import('./cards'));
const DevicesPage = lazy(() => import('./devices'));
const PlansPage = lazy(() => import('./plans'));
const HomePage = lazy(() => import('./home'));
const PlacesPage = lazy(() => import('./places'));
const IngredientsPage = lazy(() => import('./ingredients'));
const ProductsPage = lazy(() => import('./products'));
const SettingsPage = lazy(() => import('./settings'));
const StatsPage = lazy(() => import('./stats'));
const TransactionsPage = lazy(() => import('./transactions'));
const UsersPage = lazy(() => import('./users'));
const ConfigurePage = lazy(() => import('./configure'));
const ScannerPage = lazy(() => import('./scanner'));

export default function Content() {
  const wrapper = useRef<HTMLDivElement>(null);

  return (
    <Wrapper ref={wrapper}>
      <Title />
      <ScrollToTop wrapper={wrapper} />
      <Header />
      <PlanSubscriptionIssuesBanner />
      <Routes>
        <Route path="" element={<ConfigureBanner />} />
      </Routes>
      <Routes>
        <Route path="scanner" element={null} />
        <Route path="*" element={<OpenWidgetHandler />} />
      </Routes>
      <SelectIntegration />
      <Main>
        <Suspense fallback={<LoadingText />}>
          <PageWrapper>
            <Routes>
              <Route path="" element={<HomePage />} />
              <Route path="configure/*" element={<ConfigurePage />} />
              <Route path="settings/*" element={<SettingsPage />} />
              <Route
                path="account_roles/*"
                element={
                  <PermissionGate permission="account_roles_view">
                    <AccountRolesPage />
                  </PermissionGate>
                }
              />
              <Route
                path="accounts/*"
                element={
                  <PermissionGate permission="accounts_view">
                    <AccountsPage />
                  </PermissionGate>
                }
              />
              <Route
                path="businesses/*"
                element={
                  <PermissionGate permission="businesses_view">
                    <BusinessesPage />
                  </PermissionGate>
                }
              />
              <Route
                path="cards/*"
                element={
                  <PermissionGate permission="cards_view">
                    <CardsPage />
                  </PermissionGate>
                }
              />
              <Route
                path="devices/*"
                element={
                  <PermissionGate permission="devices_view">
                    <DevicesPage />
                  </PermissionGate>
                }
              />
              <Route
                path="places/*"
                element={
                  <PermissionGate permission="places_view">
                    <PlacesPage />
                  </PermissionGate>
                }
              />
              <Route
                path="plans/*"
                element={
                  <PermissionGate permission="plans_view">
                    <PlansPage />
                  </PermissionGate>
                }
              />
              {shouldDisplayIngredients ? (
                <Route
                  path="ingredients/*"
                  element={
                    <PermissionGate permission="ingredients_view">
                      <IngredientsPage />
                    </PermissionGate>
                  }
                />
              ) : null}
              <Route
                path="products/*"
                element={
                  <PermissionGate permission="products_view">
                    <ProductsPage />
                  </PermissionGate>
                }
              />
              <Route
                path="scanner/*"
                element={
                  <PermissionGate permission="scanner_view">
                    <ScannerPage />
                  </PermissionGate>
                }
              />
              <Route
                path="stats/*"
                element={
                  <PermissionGate permission="stats_view">
                    <StatsPage />
                  </PermissionGate>
                }
              />
              <Route
                path="transactions/*"
                element={
                  <PermissionGate permission="transactions_view">
                    <TransactionsPage />
                  </PermissionGate>
                }
              />
              <Route
                path="users/*"
                element={
                  <PermissionGate permission="users_view">
                    <UsersPage />
                  </PermissionGate>
                }
              />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </PageWrapper>
        </Suspense>
      </Main>
      <Footer />
    </Wrapper>
  );
}
